import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin } from "lucide-react";
import { Reveal } from "./Reveal";
import { trackLead } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const InputBase = "w-full bg-white border border-black/20 px-4 py-3 text-base focus:outline-none focus:ring-1 focus:ring-[#0a0a0a] focus:border-[#0a0a0a] transition-colors";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your name");
    if (!form.email.trim() && !form.phone.trim()) return toast.error("Add an email or phone so we can reach you");
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        source: "contact",
        name: form.name,
        email: form.email || null,
        phone: form.phone || null,
        message: form.message || null
      });
      trackLead({ source: "contact" });
      toast.success("Thanks! Melissa will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#0a0a0a] text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="eyebrow text-gold mb-4">Get In Touch</p>
            <h2 className="font-heading font-light text-4xl sm:text-5xl tracking-tight leading-tight">
              Let's start a conversation
            </h2>
            <p className="text-white/70 font-light text-lg mt-6 leading-relaxed">
              Have a question or ready to make a move? Send a note and Melissa will personally
              reach out.
            </p>
            <div className="mt-10 space-y-5">
              <a href="mailto:melissarelocationexpert@gmail.com" data-testid="contact-email-link" className="flex items-center gap-4 text-white/80 hover:text-gold transition-colors">
                <Mail size={20} className="text-gold" /> melissarelocationexpert@gmail.com
              </a>
              <a href="tel:+19513343178" data-testid="contact-phone-link" className="flex items-center gap-4 text-white/80 hover:text-gold transition-colors">
                <Phone size={20} className="text-gold" /> (951) 334-3178
              </a>
              <p className="flex items-center gap-4 text-white/80">
                <MapPin size={20} className="text-gold" /> Serving Redlands, Palm Springs & Temecula, CA
              </p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <form onSubmit={submit} data-testid="contact-form" className="grid sm:grid-cols-2 gap-4">
              <input data-testid="contact-name" className={InputBase} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input data-testid="contact-phone" className={InputBase} placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <input data-testid="contact-email-input" type="email" className={`${InputBase} sm:col-span-2`} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <textarea data-testid="contact-message" rows={5} className={`${InputBase} sm:col-span-2 resize-none`} placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              <button data-testid="contact-submit-button" type="submit" disabled={loading} className="sm:col-span-2 bg-gold text-white py-4 text-sm tracking-wide hover:bg-gold-hover transition-colors disabled:opacity-60">
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => (
  <footer data-testid="footer" className="bg-[#0a0a0a] border-t border-white/10 text-white/60">
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
      <div>
        <p className="font-heading text-xl text-white">Melissa O'Donnell Real Estate Group</p>
        <p className="text-sm mt-2 font-light">Real Broker · DRE# 01810851</p>
        <a href="tel:+19513343178" className="text-sm mt-1 font-light hover:text-gold transition-colors inline-block">(951) 334-3178</a>
      </div>
      <p className="text-xs font-light">© {new Date().getFullYear()} Melissa O'Donnell. All rights reserved.</p>
    </div>
  </footer>
);
